/**
 * Enhanced API Utility with Token Manager
 * - Uses centralized token manager
 * - Automatic token refresh with race condition prevention
 * - Automatic request retry after successful token refresh
 * - Auto logout and redirect on auth failure
 * - Better error handling with custom events
 */

import { ofetch } from 'ofetch'
import { tokenManager } from '@/utils/tokenManager'
import { apiLogger } from '@/utils/logger'

export const $api = ofetch.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },

  async onRequest({ request, options }) {
    // Skip token validation for public endpoints (login, refresh)
    const url = request.toString()
    const isPublicEndpoint = url.includes('/auth/login') || url.includes('/auth/refresh')

    if (isPublicEndpoint)
      return

    // Get valid token (with auto-refresh if needed)
    const accessToken = await tokenManager.getValidAccessToken()

    if (accessToken) {
      // Set Authorization header properly
      options.headers = {
        ...(options.headers || {}),
        Authorization: `Bearer ${accessToken}`,
      } as any
    }
  },

  async onResponseError({ response, request, options }) {
    // Handle 401 Unauthorized
    if (response.status === 401) {
      apiLogger.warn('Received 401 Unauthorized - Token invalid or expired')

      // Special handling for refresh token endpoint failure
      const isRefreshEndpoint = request.toString().includes('/auth/refresh')
      const isLogoutEndpoint = request.toString().includes('/auth/logout')

      if (isRefreshEndpoint) {
        apiLogger.error('[API] Refresh token endpoint failed - Token is invalid/expired')
        apiLogger.warn('[API] Forcing IMMEDIATE redirect to login...')

        // Clear tokens WITHOUT calling refresh again (prevent infinite loop)
        await tokenManager.clearTokens(true)

        // Force redirect to login page immediately
        if (typeof window !== 'undefined')
          window.location.replace('/login')

        return
      }

      // Special handling for logout endpoint - token already expired, no need to refresh
      if (isLogoutEndpoint) {
        apiLogger.debug('[API] Logout endpoint returned 401 - Token already expired, skipping refresh')

        return
      }

      // Prevent infinite retry loop
      if ((options as any)._retried) {
        apiLogger.warn('[API] Request already retried, not retrying again')

        return
      }

      // For other endpoints, try to refresh token
      // Note: tokenManager has singleton pattern to prevent multiple refresh requests
      // If multiple requests get 401 simultaneously, they will wait for single refresh
      apiLogger.debug('[API] Attempting token refresh for retry...')

      const refreshSuccess = await tokenManager.refreshToken()

      if (refreshSuccess) {
        apiLogger.info('[API] Token refreshed successfully')

        // Get new token
        const newToken = tokenManager.getAccessToken()

        if (newToken) {
          apiLogger.debug('[API] Retrying request with new token...')

          // Mark as retried to prevent infinite loop
          const retryOptions = {
            ...options,
            _retried: true,
            headers: {
              ...(options.headers || {}),
              Authorization: `Bearer ${newToken}`,
            },
          }

          // Retry the request with new token
          // Note: We throw the result to break the error handler chain
          // This allows ofetch to return the retry result to the caller
          try {
            const url = request.toString()

            // Throw the successful result to break error handler chain
            throw await $api(url, retryOptions)
          }
          catch (retryError) {
            // If it's our retry result, re-throw it
            if (!(retryError instanceof Error))
              throw retryError

            console.error('❌ [API] Retry failed:', retryError)
            throw retryError
          }
        }
        else {
          console.error('❌ [API] No new token available after refresh')
        }
      }
      else {
        // If refresh failed, tokenManager already handled redirect
        // Just log here, don't redirect again (prevent double redirect)
      }
    }

    // Handle 403 Forbidden
    if (response.status === 403) {
      console.error('❌ 403 Forbidden - Insufficient permissions')

      if (typeof window !== 'undefined') {
        const event = new CustomEvent('api-error', {
          detail: {
            status: 403,
            message: 'You do not have permission to perform this action',
          },
        })

        window.dispatchEvent(event)
      }
    }

    // Handle server errors
    if (response.status >= 500) {
      console.error('❌ Server error:', response.status)

      if (typeof window !== 'undefined') {
        const event = new CustomEvent('api-error', {
          detail: {
            status: response.status,
            message: 'Server error occurred. Please try again later.',
          },
        })

        window.dispatchEvent(event)
      }
    }
  },
})
